import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  AlertTriangle, 
  Clock, 
  TrendingUp, 
  Calendar,
  X,
  Search,
  Filter
} from "lucide-react";

// Alertas reais chegam por prop (derivados dos RDOs/obras no RecentAlerts)
export interface AlertModalItem {
  id: string;
  icon: typeof AlertTriangle;
  title: string;
  description: string;
  time: string;
  severity: "high" | "medium" | "low";
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'high':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'medium':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'low':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const getIconColor = (severity: string) => {
  switch (severity) {
    case 'high':
      return 'text-destructive';
    case 'medium':
      return 'text-warning';
    case 'low':
      return 'text-muted-foreground';
    default:
      return 'text-muted-foreground';
  }
};

interface AllAlertsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Alertas reais derivados (RecentAlerts) */
  alerts?: AlertModalItem[];
}

export function AllAlertsModal({ open, onOpenChange, alerts = [] }: AllAlertsModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState<string>("all");

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === "all" || alert.severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Todos os Alertas
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col space-y-4 flex-1 overflow-hidden">
          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar alertas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background text-foreground"
              >
                <option value="all">Todas severidades</option>
                <option value="high">Alta</option>
                <option value="medium">Média</option>
                <option value="low">Baixa</option>
              </select>
            </div>
          </div>

          {/* Alert count */}
          <div className="text-sm text-muted-foreground">
            {filteredAlerts.length} {filteredAlerts.length === 1 ? 'alerta encontrado' : 'alertas encontrados'}
          </div>

          {/* Alerts list */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {filteredAlerts.map((alert) => (
              <div 
                key={alert.id}
                className="group relative flex items-start space-x-4 p-4 rounded-lg border hover:bg-accent/50 transition-colors"
              >
                <div className={`p-2 rounded-full ${getSeverityColor(alert.severity)}`}>
                  <alert.icon className={`h-5 w-5 ${getIconColor(alert.severity)}`} />
                </div>
                
                <div className="flex-1 min-w-0 pr-8">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-foreground text-base">
                      {alert.title}
                    </h4>
                    <Badge 
                      variant="outline"
                      className={`text-xs ml-2 ${getSeverityColor(alert.severity)}`}
                    >
                      {alert.severity === 'high' ? 'Alta' : 
                       alert.severity === 'medium' ? 'Média' : 'Baixa'}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">
                    {alert.description}
                  </p>
                  
                  <div className="flex items-center justify-end text-xs text-muted-foreground">
                    <span>{alert.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredAlerts.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum alerta encontrado</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}